import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Section } from '../../../components/Section';
import { Text } from '../../../components/Text';
import { EditAtiraLinkDto } from '../../../model/atira-link/dto/EditAtiraLinkDto';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { AtiraLinkActions } from '../../../redux/atira-link/atira-link.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { Switch } from '../../../utils/local-antd';
import { onSetTypedCRUDErrors } from '../../../utils/onSetTypedCRUDErrors';
import { LinkInput } from '../../my-link/components/LinkInput';

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 10rem;
  margin-top: ${Spacing.l};

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

export const SettingsGoogleAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const { control, getValues, reset, watch, setError } =
    useForm<EditAtiraLinkDto>();

  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const selectedAtiraLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );

  const inputDisabled = !watch('settings.analytics.googleAnalytics.enabled');

  const onSave = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      await dispatch(AtiraLinkActions.editAtiraLink(dto)).unwrap();
      await dispatch(AtiraLinkActions.getUserAtiraLinks({ userId })).unwrap();

      AtiraToast.success(t('settings.customAnalytics.save.success'));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reset({
      atiraLinkId: selectedAtiraLink?._id,
      userId,
      settings: {
        analytics: {
          googleAnalytics: {
            trackingId:
              selectedAtiraLink?.settings?.analytics?.googleAnalytics
                ?.trackingId,
            enabled:
              selectedAtiraLink?.settings?.analytics?.googleAnalytics?.enabled,
          },
        },
      },
    });
  }, [reset, selectedAtiraLink, userId]);

  return (
    <Section title={t('settings.customAnalytics.google.title')}>
      <Text color="fgSecondary">
        {t('settings.customAnalytics.google.description')}
      </Text>

      <Controller
        control={control}
        name="settings.analytics.googleAnalytics.enabled"
        render={({ field: { value, onChange } }) => (
          <Flex alignItems="center" gap="s">
            <Text>{t('common.enabled')}</Text>
            <Switch value={value} onChange={onChange} />
          </Flex>
        )}
      />

      <Controller
        control={control}
        name="settings.analytics.googleAnalytics.trackingId"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <LinkInput
            value={value}
            onChange={onChange}
            placeholder="G-XXXXXXXXXX"
            disabled={inputDisabled}
            title={t('settings.customAnalytics.google.title')}
            errorMessage={error?.message}
            valid={!error}
          />
        )}
      />

      <StyledButton onClick={onSave} loading={loading}>
        {t('common.save')}
      </StyledButton>
    </Section>
  );
};
